<template>
  <v-card flat class="rmr-table-shell">
    <div class="rmr-table-shell__header">
      <div class="above-table">
        <rmr-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Recherche..."
        />
        <rmr-select
          v-model="roleFilter"
          :items="roles"
          item-text="name"
          item-value="id"
          label="Filtrer par rôle"
          clearable
          class="role-filter"
        />
      </div>
    </div>

    <v-card-text class="rmr-table-shell__body">
      <v-data-table
        locale="fr-FR"
        :page="options.page"
        :pageCount="pageCount"
        :headers="headers"
        :items="filteredItems"
        :sort-by="'updated'"
        :sort-desc="true"
        :options.sync="options"
        :loading="loading"
        class="rmr-data-table"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100],
        }"
        :items-per-page="options.itemsPerPage"
      >
        <template v-slot:loading>
          <div class="rmr-data-table__state">
            <v-progress-circular indeterminate color="#6366F1" size="28" class="mb-2" />
            <div class="rmr-data-table__state-title">Chargement des utilisateurs</div>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="rmr-data-table__state">
            <div class="rmr-data-table__state-title">
              {{ hasActiveFilters ? "Aucun utilisateur trouvé" : "Aucun utilisateur disponible" }}
            </div>
            <div class="rmr-data-table__state-text">
              {{
                hasActiveFilters
                  ? "Aucun compte ne correspond aux filtres actuels."
                  : "Créez un premier utilisateur pour démarrer la gestion des accès."
              }}
            </div>
          </div>
        </template>

        <template v-slot:item.roles_display="{ item }">
          <rmr-keyword-chips
            v-if="item.roles_display.length"
            :keywords="item.roles_display"
            :max-visible="3"
          />
          <span v-else class="text-caption grey--text">Aucun rôle</span>
        </template>
        <template v-slot:item.last_login="{ item }">
          <span v-if="item.last_login">
            {{ formatDateTimeFrench(item.last_login) }}
          </span>
          <span v-else>Jamais</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex" style="gap: 8px">
            <rmr-icon-button
              :icon="icons.mdiPencil"
              variant="edit"
              tooltip="Modifier l'utilisateur"
              @click="$emit('edit', item)"
            />
            <rmr-icon-button
              :icon="icons.mdiTrashCan"
              variant="delete"
              tooltip="Supprimer l'utilisateur"
              @click="
                dialogModel.dialog = true;
                dialogModel.id = item.id;
                dialogModel.entryName = item.name;
              "
            />
          </div>
        </template>
      </v-data-table>
    </v-card-text>
    <rmr-confirm-dialog
      v-model="dialogModel.dialog"
      title="Confirmation Suppression"
      :message="`Le compte de l'utilisateur '${dialogModel.entryName}' sera supprimé.`"
      confirm-label="Supprimer"
      cancel-label="Annuler"
      danger
      @confirm="deleteEntry"
    />
  </v-card>
</template>
<style scoped></style>
<script>
import axios from "@axios";
import store from "@/store";
import RmrConfirmDialog from "@/components/ui/actions/RmrConfirmDialog.vue";
import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import RmrKeywordChips from "@/components/ui/feedback/RmrKeywordChips.vue";
import RmrSelect from "@/components/ui/forms/RmrSelect.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import { mdiPencil, mdiTrashCan } from "@mdi/js";
import debounce from "lodash/debounce";
import { formatDateTimeFrench } from "@/utils/dates";

export default {
  name: "UsersTable",
  components: {
    RmrConfirmDialog,
    RmrIconButton,
    RmrKeywordChips,
    RmrSelect,
    RmrTextField,
  },
  created() {
    this.debouncedUpdateData();
  },
  data() {
    return {
      search: "",
      roleFilter: null,
      items: [],
      roles: [],
      loading: false,
      total: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        total: 0,
      },
      dialogModel: {
        dialog: false,
        id: -1,
        entryName: "",
      },
      headers: [
        {
          text: "ID",
          value: "id",
        },
        {
          text: "Nom",
          align: "start",
          value: "name",
        },
        {
          text: "Email",
          value: "email",
        },
        {
          text: "Rôles",
          value: "roles_display",
        },
        {
          text: "Page d'accueil",
          value: "home_page_url",
        },
        {
          text: "Date de dernière connexion",
          value: "last_login",
        },
        {
          value: "actions",
        },
      ],
      icons: {
        mdiPencil,
        mdiTrashCan,
      },
    };
  },
  watch: {
    options: {
      handler() {
        this.debouncedUpdateData();
      },
    },
    search() {
      this.options.page = 1;
    },
    roleFilter() {
      this.options.page = 1;
    },
  },
  computed: {
    filteredItems() {
      const searchText = (this.search || "").toLowerCase().trim();

      return this.items.filter((item) => {
        const matchesSearch =
          !searchText ||
          [item.name, item.email, item.home_page_url, ...(item.roles_display || [])]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(searchText));

        if (!matchesSearch) {
          return false;
        }

        return !this.roleFilter || (item.role_ids || []).includes(this.roleFilter);
      });
    },
    pageCount() {
      return Math.ceil(this.filteredItems.length / this.options.itemsPerPage) || 1;
    },
    hasActiveFilters() {
      return Boolean(this.search || this.roleFilter);
    },
  },
  methods: {
    formatDateTimeFrench,
    debouncedUpdateData: debounce(async function () {
      await this.updateData();
    }, 500),
    async updateData() {
      this.loading = true;
      const { data } = await axios.get("/users/");
      this.loading = false;
      this.items = (data.items || []).map(item => ({
        ...item,
        roles_display: (item.roles || []).map(role => role.name),
      }));
      this.total = data.total;
    },
    async loadRoles() {
      const { data } = await axios.get("/roles/");
      this.roles = data.items || [];
    },
    async deleteEntry() {
      const { status } = await axios.delete(`/users/${this.dialogModel.id}`);
      if (status === 200) {
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `Le compte de l'utilisateur '${this.dialogModel.entryName}' a ete supprime.`,
          color: "success",
          timeout: 4000,
        });
        this.dialogModel.dialog = false;
        this.debouncedUpdateData();
      }
    },
    async reload() {
      await Promise.all([this.updateData(), this.loadRoles()]);
    },
  },
  async mounted() {
    await this.loadRoles();
  },
};
</script>
<style lang="scss" scoped>
.above-table {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
}

.role-filter {
  max-width: none;
}

@media (max-width: 960px) {
  .above-table {
    grid-template-columns: 1fr;
  }

  .role-filter {
    max-width: none;
  }
}
</style>
